import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'antd';
import GlobalButton from '../components/GlobalButton';
import CrossCircle from '../assets/svgIcons/CrossCircle';
import { NewCopyIcon } from 'Components/common/assets/svgIcons/NewCopyIcon';
import { EditIcon } from '../assets/svgIcons/EditIcon';
import GreenTick from '../assets/svgIcons/GreenTick';
import { RedCrossIcon } from '../assets/svgIcons/RedCrossIcon';
import { GilroySmallIcon } from 'Components/common/assets/svgIcons/GilroySmallIcon';
import { actions } from 'Slice/AlertsSlice';
import {
  selectFeedPeopleNotificationHighlightsLoading,
  selectFeedPeopleNotificationHighlights
} from '../../../Selector/AlertsSelector';
import { loadingGifV6 } from '../../../Utils/utilsStyle';
import {
  DynamicHighlightResponse,
  ModalSection,
  NotificationHighlightEmailDraft,
  NotificationHighlightValue
} from 'Types/CompaniesTypes';
import HeadingWithToolTip from '../components/HeadingWithToolTip';
import HTMLReactParser from 'html-react-parser';
import { feedHighlightsModalTooltipContent, valueToObject } from '../../../Utils/UtilityFunctions';
import GeneralStyleHighlights from 'Style/GeneralStyleHighlights';
import { NEXT_BEST_ACTIONS } from '../../../Utils/Constants';
import { actions as flurryActions } from 'Slice/HelperSlice';

// Types
interface HighlightsModalProps {
  visible: boolean;
  onClose: () => void;
  initialSection?: ModalSection;
  notificationId: number;
  personId: number;
}

interface SectionConfig {
  title: string | React.ReactNode;
  hasFooter?: boolean;
  component: React.ReactNode;
}

const toStringList = (value: NotificationHighlightValue | undefined): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return [value];
  }

  return [];
};

const toDisplayText = (value: NotificationHighlightValue | undefined): string => {
  if (Array.isArray(value)) {
    return value.join('\n');
  }

  return typeof value === 'string' ? value : '';
};

const toEmailDraft = (value: NotificationHighlightValue | undefined): NotificationHighlightEmailDraft | undefined => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as NotificationHighlightEmailDraft;
  }

  return undefined;
};

// Sub-components
const CopyableTextArea: React.FC<{ text: string; onSave?: (newText: string) => void; showCopyIcon?: boolean; onEditingChange?: (isEditing: boolean) => void }> = ({ text, onSave, showCopyIcon = true, onEditingChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(text);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emptyTextPlaceholder = 'Type your prompt here and get quick insights from Gilroy instantly';
  const hasText = editedText.trim().length > 0;
  const enableSubmit = hasText && editedText.trim() !== text.trim();
  const displayText = hasText ? editedText : emptyTextPlaceholder;

  useEffect(() => {
    if (!isEditing) {
      setEditedText(text);
    }
  }, [text, isEditing]);

  useEffect(() => {
    onEditingChange?.(isEditing);
  }, [isEditing, onEditingChange]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(editedText);
      setCopied(true);

      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }

      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const handleSave = () => {
    onSave?.(editedText);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedText(text);
    setIsEditing(false);
  };

  const handleTextareaKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing) {
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!enableSubmit) return;
      else
        handleSave();
    }
  };

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const el = textareaRef.current;
      el.focus();

      // Move cursor to end
      const length = el.value.length;
      el.setSelectionRange(length, length);
    }
  }, [isEditing]);


  return (
    <div className="copyable-textarea">
      {!isEditing && showCopyIcon && (
        <div className="copy-icon" onClick={handleCopy}>
          <NewCopyIcon />
          {copied && <span className="copy-tooltip">Copied!</span>}
        </div>
      )}

      {isEditing ? (
        <div className="edit-mode">
          <textarea
            ref={textareaRef}
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            onKeyDown={handleTextareaKeyDown}
            className="editable-textarea"
            rows={4}
          />
          <div className="edit-actions">
            <span
              className="close-icon"
              onClick={handleCancel}
              role="button"
              tabIndex={0}
            >
              <RedCrossIcon />
            </span>
            <span
              className={`action-icon save-icon ${!enableSubmit ? 'disabled' : ''}`}
              tabIndex={enableSubmit ? 0 : -1}
              role="button"
              onKeyDown={enableSubmit ? handleSave : undefined}
              onClick={enableSubmit ? handleSave : undefined}
              aria-label="Save edits"
              aria-disabled={!enableSubmit}
              style={!enableSubmit ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              <GreenTick />
            </span>
          </div>
        </div>
      ) : (
        <div className="text-content" onClick={(e) => { if (!showCopyIcon) setIsEditing(true); }}>
          {!showCopyIcon ? (
            <pre className={!hasText ? 'empty-placeholder' : undefined}>{displayText}</pre>
          )
            : (
              <pre className='pre2'>{editedText}</pre>
            )
          }
          {onSave && (
            <div className="edit-icon" onClick={() => setIsEditing(true)}>
              <EditIcon />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const InfoCard: React.FC<{ title: string; content: string }> = ({ title, content }) => (
  <div className="info-card">
    {title ? <h3 className='sections-title'>{title}</h3> : null}
    <div className="textbox">
      <p>{content}</p>
    </div>
  </div>
);

const BulletList: React.FC<{ items: string[]; title?: string }> = ({ items, title }) => (
  <div className="bullet-list-section">
    {title && <h3 className="section-subtitle">{title}</h3>}
    <ul className="bullet-list">
      {items.map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ul>
  </div>
);

const OrderedList: React.FC<{ items: string[]; title?: string }> = ({ items, title }) => (
  <div className="ordered-list-section">
    {title && <h3 className="section-subtitle">{title}</h3>}
    <ol className="ordered-list">
      {items.map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ol>
  </div>
);

const HtmlInfoCard: React.FC<{ title: string; content: React.ReactNode }> = ({ title, content }) => (
  <div className="info-card">
    {title ? <h3 className='sections-title'>{title}</h3> : null}
    <div className="textbox" style={{ whiteSpace: 'pre-wrap' }}>
      <p>{content}</p>
    </div>
  </div>
);

const GilroySection: React.FC<{
  highlightsData?: DynamicHighlightResponse;
  fetchNotificationHighlights: (isRefresh?: boolean, prompt?: string) => void;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  onPromptStateChange?: (state: { prompt: string; isEditing: boolean }) => void;
  personId: number;
}> = ({ highlightsData, fetchNotificationHighlights, loading, setLoading, onPromptStateChange, personId }) => {
  const initialPrompt = toDisplayText(highlightsData?.['prompt']) || '';
  const [prompt, setPrompt] = useState(initialPrompt);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const dispatch = useDispatch();
  const host = window.location.href;

  useEffect(() => {
    onPromptStateChange?.({ prompt, isEditing: isEditingPrompt });
  }, [prompt, isEditingPrompt, onPromptStateChange]);

  const handleSavePrompt = (newPrompt: string) => {
    setPrompt(newPrompt);
    setLoading(true);
    fetchNotificationHighlights(true, newPrompt);
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('Highlight_Custom_Prompt_Submitted', 0, { host, personId, tags_input: 'Person', search_query: newPrompt })
    ));
  };

  return (
    <div className="gilroy-section">
      <div className="prompt-section">
        <h3 className="highlight-modal-section-title">Add your Prompt</h3>
        <CopyableTextArea
          text={prompt}
          onSave={handleSavePrompt}
          showCopyIcon={false}
          onEditingChange={setIsEditingPrompt}
        />
      </div>

      {(loading) ? (
        <div className='loading-gilroy'>
          <img className="loader-gif" src={loadingGifV6} alt="Loading..." />
        </div>
      )
        : (
          <>
            <div className="insights-section">
              {toStringList(highlightsData?.['Key Insights']).length > 0 && (
                <BulletList
                  title="Key Insights"
                  items={toStringList(highlightsData?.['Key Insights'])}
                />
              )}
            </div>

            <div className="actions-section">
              {toStringList(highlightsData?.['Recommended Actions']).length > 0 && (
                <BulletList
                  title="Recommended Actions"
                  items={toStringList(highlightsData?.['Recommended Actions'])}
                />
              )}
              {toDisplayText(highlightsData?.['response']) && (
                <HtmlInfoCard
                  title=""
                  content={HTMLReactParser(toDisplayText(highlightsData?.['response']))}
                />
              )}
            </div>
          </>
        )}
    </div>
  );
};

// Main Modal Component
const HighlightsModal: React.FC<HighlightsModalProps> = ({ visible, onClose, initialSection = 'sales_insights', notificationId, personId }) => {
  const activeSection = initialSection;
  const dispatch = useDispatch();
  const highlightsLoading = useSelector(selectFeedPeopleNotificationHighlightsLoading);
  const highlightsData = useSelector(selectFeedPeopleNotificationHighlights) as DynamicHighlightResponse | undefined;
  const [gilroyInsightLoading, setGilroyInsightLoading] = useState(false);
  const [gilroyPromptState, setGilroyPromptState] = useState({ prompt: '', isEditing: false });
  const host = window.location.href;
  const FLURRY_EVENT_NAMES: Record<string, string> = {
    'sales_insights': 'Highlight_Sales_Angle_Regenerated',
    'personalized_outreach': 'Highlight_Outreach_Message_Regenerated',
    'role_specific_challenges': 'Highlight_Role_Challenges_Regenerated',
    'engagement_opportunities': 'Highlight_Engagement_Opportunities_Regenerated',
    'gilroy_insight': 'Highlight_Quick_Gilroy_Regenerate',
  };
  const fetchNotificationHighlights = (isRefresh?: boolean, prompt?: string) => {
    dispatch(actions.getNotificationHighlights({
      notificationId,
      page: activeSection,
      ...(isRefresh ? { refresh: true } : {}),
      ...(typeof prompt === 'string' ? { prompt } : {})
    }));
  };

  useEffect(() => {
    fetchNotificationHighlights();
  }, []);

  useEffect(() => {
    if (!highlightsLoading && gilroyInsightLoading) {
      setGilroyInsightLoading(false);
    }
  }, [highlightsLoading, gilroyInsightLoading]);

  // Sales Insights Content
  const SalesInsightsContent = () => {
    const transitionMatters = toDisplayText(highlightsData?.['Why This Transition Matters']);
    const keySellingPoints = toStringList(highlightsData?.['Key selling points']);
    const strategicAngles = toDisplayText(highlightsData?.['Strategic angles for outreach']);
    const opportunityMapping = toDisplayText(highlightsData?.['Opportunity mapping']);

    return (
      <div className="modal-content sales-insights">
        {transitionMatters && (
          <InfoCard
            title="Why This Transition Matters"
            content={transitionMatters}
          />
        )}
        {keySellingPoints.length > 0 && (
          <BulletList
            title="Key Pain Points to Address"
            items={keySellingPoints}
          />
        )}
        {strategicAngles && (
          <InfoCard
            title="Recommended Approach"
            content={strategicAngles}
          />
        )}
        {opportunityMapping && (
          <InfoCard
            title="Timing Advantage"
            content={opportunityMapping}
          />
        )}
      </div>
    );
  };

  // Outreach Message Content
  const OutreachMessageContent = () => {
    const emailDraft = toEmailDraft(highlightsData?.['Email Draft']);
    const linkedInDraft = toDisplayText(highlightsData?.['LinkedIn Message Draft']);
    const emailText = emailDraft
      ? `Subject: ${emailDraft.subject || ''}\n\n${emailDraft.body || ''}`
      : '';

    return (
      <div className="modal-content outreach-message">
        <h3 className="highlight-modal-section-title">Suggested Email</h3>
        {emailText && <CopyableTextArea text={emailText} />}

        <h3 className="highlight-modal-section-title" style={{ marginTop: 24 }}>LinkedIn Message</h3>
        {linkedInDraft && (
          <CopyableTextArea text={linkedInDraft} />
        )}
      </div>
    );
  };

  // Role Transition Challenges Content
  const RoleChallengesContent = () => {
    const immediateChallenges = toStringList(highlightsData?.['Immediate Challenges (0\u201330 Days)']);
    const shortTermChallenges = toStringList(highlightsData?.['Short-Term Challenges (30\u201390 Days)']);
    const strategicChallenges = toStringList(highlightsData?.['Strategic Challenges (90+ Days)']);

    return (
      <div className="modal-content role-challenges">
        {immediateChallenges.length > 0 && (
          <BulletList
            title="Immediate Challenges (0-30 Days)"
            items={immediateChallenges}
          />
        )}
        {shortTermChallenges.length > 0 && (
          <BulletList
            title="Short-Term Challenges (30-90 Days)"
            items={shortTermChallenges}
          />
        )}
        {strategicChallenges.length > 0 && (
          <BulletList
            title="Strategic Challenges (90+ Days)"
            items={strategicChallenges}
          />
        )}
        <InfoCard
          title="How Your Solution Helps"
          content="Position your solution as a strategic enabler that addresses these challenges at each stage of their transition journey."
        />
      </div>
    );
  };

  // Engagement Opportunities Content
  const EngagementContent = () => {
    const conversationStarters = toStringList(highlightsData?.['Conversation starters']);
    const salesOpportunities = toStringList(highlightsData?.['Sales opportunities']);
    const engagementHooks = toStringList(highlightsData?.['Engagement hooks']);

    return (
      <div className="modal-content engagement-opportunities">
        {conversationStarters.length > 0 && (
          <BulletList
            title="Immediate Opportunities"
            items={conversationStarters}
          />
        )}
        {salesOpportunities.length > 0 && (
          <BulletList
            title="Strategic Entry Points"
            items={salesOpportunities}
          />
        )}
        {engagementHooks.length > 0 && (
          <BulletList
            title="Content to Share"
            items={engagementHooks}
          />
        )}
        <OrderedList
          title="Next Best Actions"
          items={NEXT_BEST_ACTIONS}
        />
      </div>
    );
  };

  const highlightsModalTooltipContent = (section: ModalSection) => {
    const tooltipItems = feedHighlightsModalTooltipContent[section] || [];
    return (
      <>
        {tooltipItems.map((item, index) => (
          <div className='modal-tooltip-content-wrapper' key={index}>
            <p className="tooltip-text">{item.label}</p>
          </div>
        ))}
      </>
    );
  }

  const sections: Record<ModalSection, SectionConfig> = {
    sales_insights: {
      title: (
        <div className="modal-title-wrapper d-flex align-items-center">
          <GilroySmallIcon modalMode />
          <div className='modal-title d-flex align-items-center'>
            Sales Angle Insights
            <span className='tooltip-wrapper'>
              <HeadingWithToolTip
                heightIconXSm="17px"
                toolTipText={highlightsModalTooltipContent('sales_insights')}
                toolPlacement={'bottomRight'}
              />
            </span>
          </div>
        </div>
      ),
      hasFooter: true,
      component: <SalesInsightsContent />
    },
    personalized_outreach: {
      title: (
        < div className="modal-title-wrapper d-flex align-items-center" >
          <GilroySmallIcon modalMode />
          <div className='modal-title d-flex align-items-center'>Personalized Outreach
            <span className='tooltip-wrapper'>
              <HeadingWithToolTip
                heightIconXSm="17px"
                toolTipText={highlightsModalTooltipContent('personalized_outreach')}
                toolPlacement={'bottomRight'}
              />
            </span>
          </div>
        </div >
      ),
      hasFooter: true,
      component: <OutreachMessageContent />
    },
    role_specific_challenges: {
      title: (
        < div className="modal-title-wrapper d-flex align-items-center" >
          <GilroySmallIcon modalMode />
          <div className='modal-title d-flex align-items-center'>Role Transition Challenges
            <span className='tooltip-wrapper'>
              <HeadingWithToolTip
                heightIconXSm="17px"
                toolTipText={highlightsModalTooltipContent('role_specific_challenges')}
                toolPlacement={'bottomRight'}
              />
            </span>
          </div>
        </div >
      ),
      hasFooter: true,
      component: <RoleChallengesContent />
    },
    engagement_opportunities: {
      title: (
        < div className="modal-title-wrapper d-flex align-items-center" >
          <GilroySmallIcon modalMode />
          <div className='modal-title d-flex align-items-center'>Engagement Opportunities
            <span className='tooltip-wrapper'>
              <HeadingWithToolTip
                heightIconXSm="17px"
                toolTipText={highlightsModalTooltipContent('engagement_opportunities')}
                toolPlacement={'bottomRight'}
              />
            </span>
          </div>
        </div >
      ),
      hasFooter: true,
      component: <EngagementContent />
    },
    gilroy_insight: {
      title: (
        < div className="modal-title-wrapper d-flex align-items-center" >
          <GilroySmallIcon modalMode />
          <div className='modal-title d-flex align-items-center'>Ask Gilroy
            <span className='tooltip-wrapper'>
              <HeadingWithToolTip
                heightIconXSm="17px"
                toolTipText={highlightsModalTooltipContent('gilroy_insight')}
                toolPlacement={'bottomRight'}
              />
            </span>
          </div>
        </div >
      ),
      hasFooter: true,
      component: (
        <GilroySection
          highlightsData={highlightsData}
          fetchNotificationHighlights={fetchNotificationHighlights}
          loading={gilroyInsightLoading}
          setLoading={setGilroyInsightLoading}
          onPromptStateChange={setGilroyPromptState}
          personId={personId}
        />
      )
    }
  };

  const currentSection = sections[activeSection];
  const isGilroySection = activeSection === 'gilroy_insight';
  // const isGilroyPromptEmpty = isGilroySection && gilroyPromptState.prompt.trim().length === 0;
  const isGilroyPromptEditing = isGilroySection && gilroyPromptState.isEditing;
  const isRegenerateDisabled = highlightsLoading || isGilroyPromptEditing;

  return (
    <>
      <GeneralStyleHighlights />
      <Modal
        title={currentSection.title}
        open={visible}
        onCancel={onClose}
        footer={currentSection.hasFooter ? (
          <div className="modal-footer">
            <GlobalButton
              title={'Regenerate'}
              onButtonClick={() => {
                const promptForRefresh = activeSection === 'gilroy_insight'
                  ? toDisplayText(highlightsData?.['prompt'])
                  : undefined;
                fetchNotificationHighlights(true, promptForRefresh);
                dispatch(flurryActions.callFlurryEvent(
                  valueToObject(FLURRY_EVENT_NAMES[activeSection], 0, { host, personId, tags_input: 'Person' })
                ));
              }}
              disabled={isRegenerateDisabled}
            />
          </div>
        ) : null}
        closeIcon={<CrossCircle />}
        maskClosable={false}
        width={700}
        className="highlights-modal"
      >
        {highlightsLoading && !gilroyInsightLoading ? (
          <div className='loading-gilroy'>
            <img className="loader-gif" src={loadingGifV6} alt="Loading..." />
          </div>
        ) : (
          currentSection.component
        )}
      </Modal>
    </>
  );
};

export default HighlightsModal;